import { type ConsentDto } from '@numa-group/apis.guest';
import { assert } from 'ts-essentials';
import { assign, createMachine, DoneInvokeEvent } from 'xstate';

import { AuthException, authService } from '@src/features/auth/services';
import { AuthContext, AuthEntryStep, AuthEvent, AuthState } from '@src/features/auth/state-machine/types';
import { profileInfoService } from '@src/features/profile/services';
import { GetLoginInfoResponseDto } from '@src/features/profile/services/profile-info.service';
import { guestApi } from '@src/services';

export const paths: AuthEntryStep[] = ['overview', 'login', 'forgot-password', 'sign-up'];

const id = 'auth-flow';

export const tags = {
  LOADING: 'loading',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAuthExceptionEvent = (e: any): e is DoneInvokeEvent<AuthException> => e.data && e.data instanceof AuthException;

const getMetaData = ({ email, language, brandId, redirectUrl, redirectTo }: AuthContext) => {
  assert(email && language && brandId && redirectUrl);

  const url = new URL(redirectUrl);
  url.searchParams.set('email', email);

  if (redirectTo) {
    url.searchParams.set('redirectTo', redirectTo);
  }

  return {
    language,
    brandId,
    redirectUrl: url.href,
  };
};

export const authMachine = createMachine<AuthContext, AuthEvent, AuthState>(
  {
    id,
    on: {
      OAUTH_REDIRECT: {
        target: 'overview',
        actions: ['setIsRedirecting'],
      },
      ROUTE_CHANGE: paths.map((target) => ({
        target,
        cond: (_, { step }) => step === target,
      })),
      RESET_ERROR: {
        actions: 'resetError',
      },
    },
    initial: 'overview',
    states: {
      overview: {
        initial: 'enter-data',
        entry: 'assignOverviewPath',
        onDone: {
          target: 'authenticated',
          actions: 'trackUserSignedInEvent',
        },
        on: {
          SOCIAL_LOGIN: {
            target: '.social-loading',
            actions: 'assignProvider',
          },
          RESOLVE_SOCIAL_LOGIN: {
            target: '.logged-in',
            actions: ['assignRedirectTo', 'clearProvider', 'clearRedirectTo'],
          },
          PREFILL_SIGN_UP: {
            actions: 'assignFullName',
          },
          SUBMIT_EMAIL: {
            target: '.check-email',
            actions: 'assignEmail',
          },
        },
        states: {
          'enter-data': {},
          'check-email': {
            tags: tags.LOADING,
            invoke: {
              src: 'getLoginInfo',
              onDone: [
                {
                  target: 'social-loading',
                  internal: false,
                  actions: 'assignProviderForAutoLogin',
                  cond: 'requiresSocialLogin',
                },
                {
                  target: `#${id}.login`,
                  internal: false,
                  cond: 'canLogin',
                },
                {
                  target: `#${id}.sign-up`,
                  internal: false,
                },
              ],
              onError: {
                actions: 'assignError',
              },
            },
          },
          'social-loading': {
            tags: tags.LOADING,
            invoke: {
              src: 'loginWithSocialProvider',
              onDone: {
                actions: ['storeProvider', 'storeRedirectTo'],
              },
              onError: {
                target: 'enter-data',
                actions: 'assignError',
              },
            },
          },
          'logged-in': {
            type: 'final',
          },
        },
      },

      login: {
        initial: 'enter-data',
        entry: 'assignLoginPath',
        onDone: {
          target: 'authenticated',
          actions: 'trackUserSignedInEvent',
        },
        states: {
          'enter-data': {
            on: {
              LOGIN: {
                target: 'loading',
                actions: ['assignEmail', 'assignPassword'],
              },
              FORGOT_PASSWORD: {
                target: `#${id}.forgot-password`,
                actions: ['assignEmail', 'assignForgotPasswordPath'],
              },
            },
          },
          loading: {
            tags: tags.LOADING,
            invoke: {
              src: 'login',
              onDone: 'logged-in',
              onError: [
                {
                  cond: (_, event) => {
                    assert(isAuthExceptionEvent(event));
                    return event.data.code === 'NotConfirmed';
                  },
                  target: `#${id}.sign-up.verification`,
                },
                {
                  target: 'enter-data',
                  actions: 'assignError',
                },
              ],
            },
          },
          'logged-in': {
            type: 'final',
          },
        },
      },

      'sign-up': {
        initial: 'enter-data',
        entry: ['assignSignUpPath', 'setRedirectUrl'],
        onDone: {
          target: 'authenticated',
          actions: 'trackUserSignedInEvent',
        },
        on: {
          VERIFY: {
            target: '.checking-verification',
            actions: ['assignVerificationCode', 'assignEmail'],
          },
        },
        states: {
          'enter-data': {
            on: {
              SIGN_UP: {
                target: 'loading',
                actions: ['assignEmail', 'assignPassword', 'assignFullName', 'assignGuestConsents'],
              },
            },
          },
          loading: {
            tags: tags.LOADING,
            invoke: {
              src: 'signUp',
              onDone: {
                target: 'verification',
                actions: 'trackUserSignedUpEvent',
              },
              onError: {
                target: 'enter-data',
                actions: 'assignError',
              },
            },
          },
          verification: {
            on: {
              RESEND_VERIFICATION: {
                target: 'sending-verification',
              },
            },
          },
          'checking-verification': {
            tags: tags.LOADING,
            invoke: {
              src: 'confirmSignUp',
              onDone: [
                {
                  cond: 'hasSignedUpOnSameDevice',
                  target: 'auto-login',
                  actions: 'trackUserConfirmedEvent',
                },
                {
                  target: `#${id}.login`,
                  actions: ['showConfirmationSuccess', 'trackUserConfirmedEvent'],
                },
              ],
              onError: [
                {
                  target: 'verification',
                  actions: 'assignError',
                },
              ],
            },
          },
          'sending-verification': {
            tags: tags.LOADING,
            invoke: {
              src: 'resendVerification',
              onDone: {
                actions: 'showVerificationResendSuccess',
                target: 'verification',
              },
              onError: {
                target: 'verification',
                actions: 'assignError',
              },
            },
          },
          'auto-login': {
            tags: tags.LOADING,
            invoke: {
              src: 'autoLogin',
              onDone: 'confirmed',
              onError: {
                actions: 'assignError',
              },
            },
          },
          confirmed: {
            type: 'final',
          },
        },
      },

      'forgot-password': {
        initial: 'enter-data',
        entry: ['assignForgotPasswordPath', 'setRedirectUrl'],
        onDone: {
          target: 'authenticated',
          actions: 'trackUserSignedInEvent',
        },
        on: {
          VERIFY: {
            target: '.change-password',
            actions: ['assignVerificationCode', 'assignEmail'],
          },
        },
        states: {
          'enter-data': {
            on: {
              REQUEST_RESET: {
                target: 'sending-verification',
                actions: 'assignEmail',
              },
            },
          },
          'sending-verification': {
            tags: tags.LOADING,
            invoke: {
              src: 'sendForgotPasswordEmail',
              onDone: 'verification',
              onError: [
                {
                  cond: (_, event) => {
                    assert(isAuthExceptionEvent(event));
                    return event.data.code === 'NotConfirmed';
                  },
                  target: `#${id}.sign-up.verification`,
                },
                {
                  target: 'enter-data',
                  actions: 'assignError',
                },
              ],
            },
          },
          verification: {
            on: {
              REQUEST_RESET: {
                target: 'resending-verification',
              },
            },
          },
          'resending-verification': {
            tags: tags.LOADING,
            invoke: {
              src: 'sendForgotPasswordEmail',
              onDone: {
                actions: 'showVerificationResendSuccess',
                target: 'verification',
              },
              onError: [
                {
                  cond: (_, event) => {
                    assert(isAuthExceptionEvent(event));
                    return event.data.code === 'NotConfirmed';
                  },
                  target: `#${id}.sign-up.verification`,
                },
                {
                  target: 'enter-data',
                  actions: 'assignError',
                },
              ],
            },
          },
          'change-password': {
            on: {
              CHANGE_PASSWORD: {
                actions: 'assignPassword',
                target: 'submitting',
              },
            },
          },
          submitting: {
            tags: tags.LOADING,
            invoke: {
              src: 'changePasswordAndLogin',
              onDone: 'success',
              onError: [
                {
                  cond: (_, event) => {
                    assert(isAuthExceptionEvent(event));
                    return event.data.code === 'InvalidCode';
                  },
                  target: 'verification',
                  actions: 'assignError',
                },
                {
                  target: 'enter-data',
                  actions: 'assignError',
                },
              ],
            },
          },
          success: {
            type: 'final',
          },
        },
      },

      authenticated: {
        type: 'final',
      },
    },
  },
  {
    actions: {
      assignEmail: assign((_, event) => {
        assert('email' in event);
        const { email } = event;

        return {
          email,
        };
      }),
      assignGuestConsents: assign((_, event) => {
        if (!('consents' in event)) {
          return {};
        }

        const { consents } = event;

        return {
          consents,
        };
      }),
      assignPassword: assign((_, event) => {
        assert('password' in event);
        const { password } = event;

        return {
          password,
        };
      }),
      assignProvider: assign((_, event) => {
        assert('provider' in event);
        const { provider } = event;

        return {
          provider,
        };
      }),
      assignRedirectTo: assign((_, event) => {
        assert('redirectTo' in event);
        const { redirectTo } = event;

        return {
          redirectTo,
        };
      }),
      assignFullName: assign((_, event) => {
        assert('fullName' in event);
        const { fullName } = event;

        return {
          fullName,
        };
      }),
      assignVerificationCode: assign((_, event) => {
        assert('verificationCode' in event);
        const { verificationCode } = event;

        return {
          verificationCode,
        };
      }),
      assignError: assign((_, event) => {
        assert(isAuthExceptionEvent(event));

        return {
          error: event.data.code,
        };
      }),
      assignOverviewPath: assign(() => ({ path: 'overview' })),
      assignLoginPath: assign(() => ({ path: 'login' })),
      assignSignUpPath: assign(() => ({ path: 'sign-up' })),
      assignForgotPasswordPath: assign(() => ({ path: 'forgot-password' })),

      resetError: assign({
        error: () => undefined,
      }),

      setIsRedirecting: assign(() => ({
        isRedirecting: true,
      })),

      storeProvider: ({ provider }) => {
        assert(provider);
        localStorage.setItem('provider', provider);
      },
      clearProvider: () => {
        localStorage.removeItem('provider');
      },

      storeRedirectTo: ({ redirectTo }) => {
        if (redirectTo) {
          localStorage.setItem('redirectTo', redirectTo);
        }
      },
      clearRedirectTo: () => {
        localStorage.removeItem('redirectTo');
      },

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      assignProviderForAutoLogin: assign((_, { data }: any) => {
        assert((data as GetLoginInfoResponseDto).action === 'requires-social-login');

        return {
          provider: data.provider.replace(/signinwithapple/gi, 'apple'),
        };
      }),
    },
    guards: {
      hasSignedUpOnSameDevice: () => authService.hasSignedUpOnSameDevice(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      requiresSocialLogin: (_, { data }: any) => (data as GetLoginInfoResponseDto).action === 'requires-social-login',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      canLogin: (_, { data }: any) => (data as GetLoginInfoResponseDto).action === 'login',
    },
    services: {
      resendVerification: async (context) => {
        const { email } = context;
        assert(email);
        await authService.resendVerification(email, getMetaData(context));
      },
      sendForgotPasswordEmail: async (context) => {
        const { email } = context;
        assert(email);
        await authService.forgotPassword(email, getMetaData(context));
      },
      getLoginInfo: ({ email }) => {
        assert(email);
        return profileInfoService.getLoginInfo(email);
      },
      changePasswordAndLogin: async ({ email, password, verificationCode }) => {
        assert(email && password);
        await authService.forgotPasswordSubmit(email, password, verificationCode!);
        await authService.login(email, password);
      },
      login: async ({ email, password }) => {
        assert(email);
        await authService.login(email, password!);
      },
      loginWithSocialProvider: async ({ provider }) => {
        await authService.loginWithSocialProvider(provider!);
      },
      signUp: async (context) => {
        const { email, password, fullName, consents, language } = context;

        assert(email && password && fullName);

        const [firstName, ...lastNames] = fullName?.trim().split(' ') ?? [];
        const lastName = lastNames.join(' ');
        const consentsToBePosted: ConsentDto[] = consents
          ? Object.entries(consents ?? {})
              .filter(([, shouldPost]) => shouldPost)
              .map(([consentType]) => ({ type: consentType as ConsentDto['type'] }))
          : [];

        await guestApi.postGuestConsents(
          {
            consents: consentsToBePosted,
            confirmationHost: window.origin,
            preferredLanguage: language,
            firstName,
            lastName,
          },
          { params: { email: encodeURIComponent(email) } },
        );

        await authService.signUp(email, password, getMetaData(context), {
          fullName,
        });
      },
      confirmSignUp: async ({ email, verificationCode }) => {
        assert(email && verificationCode);
        await authService.confirmSignUp(email, verificationCode);
      },
      autoLogin: async ({ email }) => {
        assert(email);
        await authService.loginAfterSignUp(email);
      },
    },
  },
);
